import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { categoryOptions, paymentTypeOptions, kidOptions } from './constants';

function RecentEntries({ refreshData }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({});
  const [message, setMessage] = useState('');
  const [expandedEntries, setExpandedEntries] = useState([]);

  const categories = categoryOptions;
  const paymentTypes = paymentTypeOptions;
  const kids = kidOptions;

  useEffect(() => {
    fetchEntries();
  }, []);

  // The fields the backend uses to confirm a row hasn't moved before editing/deleting it
  const originalOf = (entry) => ({
    date: entry.date, amount: entry.amount, reason: entry.reason, category: entry.category, who: entry.who
  });

  const errorText = (err) => err.response?.data?.error || err.message;

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/budgets', { withCredentials: true });
      // Sort by row index (most recent entries last in the spreadsheet)
      // Then take the most recent 10 entries
      const sortedEntries = [...response.data]
        .sort((a, b) => b.rowIndex - a.rowIndex)
        .slice(0, 10);
      setEntries(sortedEntries);
      setError('');
    } catch (err) {
      setError('Failed to fetch recent entries: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      date: entry.date,
      amount: entry.amount,
      kidsCheshbon: entry.kidsCheshbon || '',
      reason: entry.reason,
      category: entry.category,
      paymentType: entry.paymentType,
      who: entry.who,
      kid: entry.kid || '',
      isKidsAccount: entry.isKidsAccount === 'Yes'
    });
  };

  const handleDelete = async (entry) => {
    if (window.confirm(`Are you sure you want to delete this entry?\n\n${entry.date}: ${entry.reason} (₪${entry.amount})`)) {
      try {
        await axios.delete(`/api/budgets/${entry.rowIndex}`, {
          withCredentials: true,
          data: { original: originalOf(entry) }
        });
        setMessage('Entry deleted successfully');
        // Re-fetch: rows below the deleted one have shifted up, so local rowIndexes are now stale
        await fetchEntries();
        if (refreshData) refreshData();
      } catch (err) {
        setMessage('Error deleting entry: ' + errorText(err));
        console.error(err);
        if (err.response?.status === 409) fetchEntries();
      }
      
      setTimeout(() => setMessage(''), 3000);
    }
  };
  
  // Toggle expanded state for mobile cards
  const toggleExpand = (key) => {
    setExpandedEntries(prev => 
      prev.includes(key) 
        ? prev.filter(k => k !== key) 
        : [...prev, key]
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (selectedOption, { name }) => {
    setFormData(prev => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingEntry) return;
    
    try {
      // Prepare submission data
      const submissionData = {
        ...formData,
        kidsCheshbon: formData.kidsCheshbon === '' ? undefined : formData.kidsCheshbon,
        isKidsAccount: formData.kidsCheshbon && formData.kid ? 'Yes' : '', 
      };
      
      // Update the entry
      await axios.put(`/api/budgets/${editingEntry.rowIndex}`,
        { ...submissionData, original: originalOf(editingEntry) },
        { withCredentials: true });

      setMessage('Entry updated successfully');
      setEditingEntry(null);
      await fetchEntries();

      // Refresh parent data
      if (refreshData) refreshData();
    } catch (err) {
      setMessage('Error updating entry: ' + errorText(err));
      console.error(err);
      if (err.response?.status === 409) {
        setEditingEntry(null);
        fetchEntries();
      }
    }
    
    setTimeout(() => setMessage(''), 3000);
  };

  const cancelEdit = () => {
    setEditingEntry(null);
    setFormData({});
  };
  
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '0.5rem',
      padding: '0.25rem',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(255, 255, 255, 0.3)' : 'none',
      '&:hover': { borderColor: 'rgba(255, 255, 255, 0.3)' },
      transition: 'all 0.2s ease-in-out',
      color: 'white',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? 'rgba(255, 255, 255, 0.2)' 
        : state.isFocused 
          ? 'rgba(255, 255, 255, 0.1)' 
          : 'transparent',
      color: 'white',
      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
      padding: '0.5rem 1rem',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '0.5rem',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      zIndex: 9999,
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '0.25rem',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'white',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'rgba(255, 255, 255, 0.6)',
    }),
    input: (provided) => ({
      ...provided,
      color: 'white',
    }),
  };

  return (
    <div className="min-h-screen p-4 md:p-6">
      <div className="w-full max-w-7xl mx-auto">
        <header className="mb-8 glass p-6 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Recent Entries</h1>
          <p className="text-white text-opacity-80">View, edit, or delete your recent budget entries</p>
        </header>

        {message && (
          <div className={`mb-6 p-4 glass transition-all duration-300 animate-fadeIn ${message.includes('Error') ? 'border-red-400 border-opacity-30' : 'border-green-400 border-opacity-30'}`}>
            <p className={`text-sm font-medium ${message.includes('Error') ? 'text-red-200' : 'text-green-200'}`}>{message}</p>
          </div>
        )}

        <div className="glass-table overflow-hidden transition-all duration-300 mb-8">
          <div className="p-6">
            <div className="flex justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">Last 10 Entries</h2>
              <button 
                onClick={fetchEntries}
                className="glass-button px-4 py-2 text-white flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
                Refresh
              </button>
            </div>

            {loading ? (
              <div className="py-8 text-center">
                <svg className="animate-spin h-10 w-10 text-white mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="mt-4 text-white text-opacity-80">Loading recent entries...</p>
              </div>
            ) : error ? (
              <div className="py-8 text-center text-red-200">
                <p>{error}</p>
              </div>
            ) : entries.length === 0 ? (
              <div className="py-8 text-center text-white text-opacity-80">
                <p>No recent entries found</p>
              </div>
            ) : (
              <>
              {/* Responsive layout - Table for desktop, Cards for mobile */}
              <div className="hidden md:block overflow-x-auto">
                {/* Desktop table view */}
                <table className="min-w-full divide-y divide-white divide-opacity-20">
                  <thead>
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-white text-opacity-70 uppercase tracking-wider">Date</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-white text-opacity-70 uppercase tracking-wider">Amount</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-white text-opacity-70 uppercase tracking-wider">Kids</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-white text-opacity-70 uppercase tracking-wider">Reason</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-white text-opacity-70 uppercase tracking-wider">Category</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-white text-opacity-70 uppercase tracking-wider">Payment</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-white text-opacity-70 uppercase tracking-wider">Who</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-white text-opacity-70 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white divide-opacity-10">
                    {entries.map(entry => (
                      <tr key={entry.rowIndex} className="hover:bg-white hover:bg-opacity-5">
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-white text-opacity-90">{entry.date}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-white text-opacity-90">₪{entry.amount}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-white text-opacity-90">
                          {entry.kidsCheshbon ? `₪${entry.kidsCheshbon}` : '—'}
                          {entry.kid ? ` (${entry.kid})` : ''}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-white text-opacity-90 max-w-xs truncate">{entry.reason}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-white text-opacity-90">{entry.category}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-white text-opacity-90">{entry.paymentType}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-white text-opacity-90">{entry.who}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-white text-opacity-90">
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => handleEdit(entry)}
                              className="glass-button px-3 py-1 text-white text-sm"
                            >
                              Edit
                            </button>
                            <button 
                              onClick={() => handleDelete(entry)}
                              className="glass-button px-3 py-1 text-white text-sm hover:bg-red-500 hover:bg-opacity-20"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile card view */}
              <div className="md:hidden space-y-4">
                {entries.map(entry => {
                  // Format the date to be shorter for mobile
                  const dateObj = new Date(entry.date);
                  const shortDate = dateObj.getDate() && !isNaN(dateObj.getDate()) 
                    ? `${dateObj.getMonth()+1}/${dateObj.getDate()}`
                    : entry.date.split(' ').slice(0, 2).join(' '); // Fallback if date parsing fails
                  
                  // Use the entry's rowIndex as part of the state key
                  const expandedKey = `expanded-${entry.rowIndex}`;
                  const isExpanded = expandedEntries.includes(expandedKey);
                  
                  return (
                    <div key={entry.rowIndex} className="glass overflow-hidden">
                      {/* Card preview - always visible */}
                      <div 
                        className="p-4 flex justify-between items-center cursor-pointer"
                        onClick={() => toggleExpand(expandedKey)}
                      >
                        <div className="flex flex-col">
                          <span className="font-medium text-white truncate max-w-[180px]">{entry.reason}</span>
                          <span className="text-sm text-white text-opacity-70">{shortDate} • {entry.category}</span>
                        </div>
                        <div className="flex flex-col items-end">
                          <span className="font-semibold text-white">₪{entry.amount}</span>
                          {entry.kidsCheshbon && (
                            <span className="text-xs text-white text-opacity-70">
                              Kids: ₪{entry.kidsCheshbon}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Expanded details */}
                      {isExpanded && (
                        <div className="px-4 pb-4 pt-2 border-t border-white border-opacity-20 bg-white bg-opacity-5">
                          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                            <div className="text-white text-opacity-60">Payment</div>
                            <div className="text-white text-opacity-90">{entry.paymentType}</div>
                            
                            <div className="text-white text-opacity-60">Who</div>
                            <div className="text-white text-opacity-90">{entry.who}</div>
                            
                            {entry.kid && (
                              <>
                                <div className="text-white text-opacity-60">Kid</div>
                                <div className="text-white text-opacity-90">{entry.kid}</div>
                              </>
                            )}
                          </div>
                          
                          <div className="mt-4 flex space-x-3">
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(entry);
                              }}
                              className="glass-button flex-1 py-2 text-white font-medium text-sm"
                            >
                              Edit
                            </button>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(entry);
                              }}
                              className="glass-button flex-1 py-2 text-white font-medium text-sm hover:bg-red-500 hover:bg-opacity-20"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {/* Visual indicator for expandable content */}
                      <div className="h-1 w-full bg-white bg-opacity-10 flex justify-center">
                        <div className="w-8 h-1 rounded-full bg-white bg-opacity-30"></div>
                      </div>
                    </div>
                  );
                })}
              </div>
              </>
            )}
          </div>
        </div>

        {editingEntry && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="glass w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-white">Edit Entry</h2>
                  <button onClick={cancelEdit} className="text-white text-opacity-70 hover:text-white">
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                  <div>
                    <label className="block text-sm font-medium text-white text-opacity-80 mb-1">Date</label>
                    <input
                      name="date"
                      type="text"
                      value={formData.date || ''}
                      onChange={handleChange}
                      placeholder="MM/DD"
                      className="glass-input w-full px-4 py-2 text-white placeholder-white placeholder-opacity-50"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white text-opacity-80 mb-1">Amount (₪)</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-white text-opacity-60 sm:text-sm">₪</span>
                      </div>
                      <input
                        name="amount"
                        type="number"
                        value={formData.amount || ''}
                        onChange={handleChange}
                        placeholder="Enter amount"
                        className="glass-input w-full pl-8 pr-4 py-2 text-white placeholder-white placeholder-opacity-50"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white text-opacity-80 mb-1">Kids Cheshbon (₪)</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-white text-opacity-60 sm:text-sm">₪</span>
                      </div>
                      <input
                        name="kidsCheshbon"
                        type="number"
                        value={formData.kidsCheshbon || ''}
                        onChange={handleChange}
                        placeholder="Enter kid amount (optional)"
                        className="glass-input w-full pl-8 pr-4 py-2 text-white placeholder-white placeholder-opacity-50"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white text-opacity-80 mb-1">Reason</label>
                    <input
                      name="reason"
                      value={formData.reason || ''}
                      onChange={handleChange}
                      placeholder="What was this expense for?"
                      className="glass-input w-full px-4 py-2 text-white placeholder-white placeholder-opacity-50"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white text-opacity-80 mb-1">Category</label>
                    <Select
                      name="category"
                      value={categories.find(option => option.value === formData.category)}
                      onChange={(option) => handleSelectChange(option, { name: 'category' })}
                      options={categories}
                      styles={customStyles}
                      placeholder="Select Category"
                      className="w-full"
                      classNamePrefix="react-select"
                      required
                      menuPortalTarget={document.getElementById('dropdown-portal')}
                      menuPosition="fixed"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white text-opacity-80 mb-1">Payment Type</label>
                    <Select
                      name="paymentType"
                      value={paymentTypes.find(option => option.value === formData.paymentType)}
                      onChange={(option) => handleSelectChange(option, { name: 'paymentType' })}
                      options={paymentTypes}
                      styles={customStyles}
                      placeholder="Select Payment Type"
                      className="w-full"
                      classNamePrefix="react-select"
                      required
                      menuPortalTarget={document.getElementById('dropdown-portal')}
                      menuPosition="fixed"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white text-opacity-80 mb-1">Kid</label>
                    <Select
                      name="kid"
                      value={kids.find(option => option.value === formData.kid)}
                      onChange={(option) => handleSelectChange(option, { name: 'kid' })}
                      options={kids}
                      styles={customStyles}
                      placeholder="Select Kid (if applicable)"
                      className="w-full"
                      classNamePrefix="react-select"
                      isClearable
                      menuPortalTarget={document.getElementById('dropdown-portal')}
                      menuPosition="fixed"
                    />
                  </div>

                  <div className="md:col-span-2 mt-4 flex space-x-4">
                    <button
                      type="button"
                      onClick={cancelEdit}
                      className="glass-button flex-1 py-3 px-4 text-white font-medium hover:bg-white hover:bg-opacity-10"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="glass-button flex-1 py-3 px-4 text-white font-medium hover:bg-blue-500 hover:bg-opacity-30"
                    >
                      Save Changes
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RecentEntries;